import { Injectable } from '@angular/core';
import {
    HttpErrorResponse,
    HttpEvent,
    HttpHandler,
    HttpInterceptor,
    HttpRequest
} from '@angular/common/http';
import { MessageService } from 'primeng/api';
import { Observable, throwError } from 'rxjs';
import { catchError } from 'rxjs/operators';

@Injectable()
export class ErrorInterceptor implements HttpInterceptor {
    private readonly genericErrorMessage = 'Something went wrong. Please contact support.';
    private readonly offlineErrorMessage = 'You appear to be offline. Please check your internet connection.';
    private readonly dedupeWindowMs = 4000;
    private lastToast?: { key: string | null; message: string; at: number };

    constructor(private messageService: MessageService) { }

    intercept(request: HttpRequest<unknown>, next: HttpHandler): Observable<HttpEvent<unknown>> {
        return next.handle(request).pipe(
            catchError((error: HttpErrorResponse) => {
                const message = this.getErrorMessage(error);
                if (message) {
                    this.showToastOnce(this.getToastKey(), message);
                }

                return throwError(() => error);
            })
        );
    }

    private showToastOnce(key: string | null, message: string): void {
        const now = Date.now();
        if (
            this.lastToast &&
            this.lastToast.key === key &&
            this.lastToast.message === message &&
            now - this.lastToast.at < this.dedupeWindowMs
        ) {
            return;
        }

        this.lastToast = { key, message, at: now };
        const toastPayload: { key?: string; severity: string; summary: string; detail: string } = {
            severity: 'error',
            summary: 'Error',
            detail: message
        };

        if (key) {
            toastPayload.key = key;
        }

        this.messageService.add(toastPayload);
    }

    private getErrorMessage(error: HttpErrorResponse): string | null {
        const backendMessage = this.extractBackendMessage(error);
        const normalizedBackendMessage = (backendMessage || '').toLowerCase();

        if (this.isInternalOrExceptionalError(error.status, normalizedBackendMessage)) {
            return this.genericErrorMessage;
        }

        if (error.status === 404) {
            return 'No data found.';
        }

        if (error.status === 0 || !error.status) {
            // status 0 can be true offline OR backend unreachable; use browser network state to distinguish.
            if (!navigator.onLine) {
                return this.offlineErrorMessage;
            }

            return this.genericErrorMessage;
        }

        return backendMessage;
    }

    private getToastKey(): string | null {
        return 'bc';
    }

    private extractBackendMessage(error: HttpErrorResponse): string | null {
        const errorBody = error?.error;

        if (!errorBody) {
            return null;
        }

        if (typeof errorBody === 'string') {
            return errorBody.trim() || null;
        }

        if (typeof errorBody?.error === 'string') {
            return errorBody.error.trim() || null;
        }

        if (typeof errorBody?.message === 'string') {
            return errorBody.message.trim() || null;
        }

        if (typeof errorBody?.title === 'string') {
            return errorBody.title.trim() || null;
        }

        return null;
    }

    private isInternalOrExceptionalError(status: number | undefined, message: string): boolean {
        if (typeof status === 'number' && status >= 500) {
            return true;
        }

        if (!message) {
            return false;
        }

        return (
            message.includes('internal server error') ||
            message.includes('internal error') ||
            message.includes('exception') ||
            message.includes('object reference not set') ||
            message.includes('an error occurred while saving the entity changes')
        );
    }
}